/* eslint-disable react/require-default-props */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { Button, useDisclosure, useToast } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import _isNil from 'lodash/isNil';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import { useTopCandidatesContext } from './TopCandidatesContext';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { EngagedConfirmationModal } from './EngagedConfirmationModal';

interface EngageCandidateProps {
  positionId: string;
  handleActionAfterEngage: (seekerId: string) => void;
  handleBeforeEngage?: (before: () => Promise<void>) => Promise<void>;
  disabled?: boolean;
  buttonSize?: string;
  givenPositionId?: string;
  givenSeekerId?: string;
}

export default function EngageCandidate({
  positionId,
  handleActionAfterEngage,
  handleBeforeEngage = () => Promise.resolve(),
  disabled = false,
  buttonSize,
  givenPositionId,
  givenSeekerId,
}: EngageCandidateProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const { isOpen: isEnagageModalOpen, onOpen: onEnagageModalOpen, onClose: onEnagageModalClose } = useDisclosure();
  const { currentUserProfile } = useCurrentUserProfile();
  const { selectedTopCandidate, positionDocument } = useTopCandidatesContext();

  const { unlock } = useStoreActions((action) => action.positionManagement);
  const accountStore = useStoreState((s) => s.app.accounts);
  const isModernHireEnable = accountStore?.configuration?.modernHireEnable ?? false;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const handleEngageCandidate = async (pId: string, sId: string, recruiterId: string, modernHireEnabled?: boolean) => {
    try {
      await unlock({
        positionId: pId,
        seekerId: sId,
        recruiterId,
        modernHireEnable: modernHireEnabled ?? false,
      });
      handleActionAfterEngage(sId);
    } catch (error) {
      let defaultMessage = t('businessManagement:topCandidate.engageErrorText');
      const newError = error as AxiosError<{ message: string }>;
      if (newError.response?.status === 401) {
        defaultMessage = newError.response.data.message || defaultMessage;
      }
      toast({
        description: defaultMessage,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAppointmentRequest = () => {
    if (currentUserProfile) {
      const isBothPositionAndSeekerAvailable = givenPositionId && givenSeekerId;

      if ((positionDocument && selectedTopCandidate) || isBothPositionAndSeekerAvailable) {
        const positionsId = givenPositionId || positionDocument?.id || '';
        const seekerId = givenSeekerId || selectedTopCandidate?.seeker.id || '';
        setIsLoading(true);

        if (handleBeforeEngage) {
          return handleBeforeEngage(() =>
            handleEngageCandidate(positionsId, seekerId, currentUserProfile.id, isModernHireEnable),
          );
        }
        handleEngageCandidate(positionsId, seekerId, currentUserProfile.id, isModernHireEnable);
      }
    }
  };

  const candidatePositionId = givenPositionId && givenSeekerId ? givenPositionId : positionId;
  const candidateSeekerId = givenPositionId && givenSeekerId ? givenSeekerId : selectedTopCandidate?.seeker?.id;

  return (
    <>
      <Button
        isDisabled={isLoading || disabled}
        isLoading={isLoading}
        variant="solid"
        colorScheme="blue"
        size={buttonSize || 'sm'}
        fontSize={{ base: '9px', md: '11px', lg: '14px' }}
        ml="5px"
        onClick={() => {
          if (!_isNil(accountStore) && accountStore.monetizationEnabled === false) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            handleCreateAppointmentRequest();
          } else {
            onEnagageModalOpen();
          }
        }}
        style={{ whiteSpace: 'pre-wrap', padding: '15px' }}
        data-testid="engageBtn"
      >
        {t('businessManagement:topCandidate.engageBtnLabel')}
      </Button>
      {isEnagageModalOpen && candidateSeekerId && candidatePositionId && (
        <EngagedConfirmationModal
          isOpen={isEnagageModalOpen}
          onClose={onEnagageModalClose}
          positionId={candidatePositionId}
          seekerId={candidateSeekerId}
          handleActionAfterEngage={handleActionAfterEngage}
          onConfirm={handleCreateAppointmentRequest}
        />
      )}
    </>
  );
}
